import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  ChevronRight,
  PenSquare,
  Rocket,
  Sparkles,
  WalletCards,
} from "lucide-react";
import { Button, MetricCard, SectionHeader, StatusPill, SurfaceCard } from "../components/designSystem";
import { getTradeUsdProfit } from "../utils/currencyDisplay";


export function getDisplayName(user) {
  return user?.name || user?.username || user?.email?.split("@")[0] || "Trader";
}

export function getPlanLabel(user) {
  if (!user?.plan) {
    return "Free";
  }

  if (user.plan === "enterprise") {
    return "Enterprise";
  }

  if (user.plan === "pro" && user.teamUpgraded === true) {
    return "Team Pro";
  }

  return user.plan.charAt(0).toUpperCase() + user.plan.slice(1);
}

export default function DashboardOverviewPage({ user, metrics, showUpgrade, hasTeam, isPaid, isAdmin, trades, overviewFeed }) {
  const equityData = trades
    .slice()
    .sort((left, right) => new Date(left.created_at) - new Date(right.created_at))
    .slice(-8)
    .reduce((result, trade, index) => {
      const lastEquity = index === 0 ? 0 : result[index - 1].equity;
      result.push({
        label: new Date(trade.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        equity: lastEquity + getTradeUsdProfit(trade),
      });
      return result;
    }, []);

  const outcomeData = [
    { name: "Wins", value: trades.filter((trade) => getTradeUsdProfit(trade) > 0).length, color: "#10b981" },
    { name: "Losses", value: trades.filter((trade) => getTradeUsdProfit(trade) <= 0).length, color: "#f43f5e" },
  ].filter((item) => item.value > 0);

  const assetBreakdown = Object.values(
    trades.reduce((accumulator, trade) => {
      const key = trade.asset || "Unknown";
      if (!accumulator[key]) {
        accumulator[key] = { asset: key, pnl: 0 };
      }
      accumulator[key].pnl += getTradeUsdProfit(trade);
      return accumulator;
    }, {})
  )
    .sort((left, right) => Math.abs(right.pnl) - Math.abs(left.pnl))
    .slice(0, 5);

  const quickActions = [
    {
      to: "/app/newtrade",
      label: "Log a trade",
      description: "Capture fresh executions with notes and pricing details.",
      icon: PenSquare,
      accent: "from-sky-500/20 to-sky-300/5 text-sky-700 dark:text-sky-300",
    },
    {
      to: "/app/analytics",
      label: "Review performance",
      description: "Inspect edge, drawdown, and behavior with stronger analytics.",
      icon: Activity,
      accent: "from-emerald-500/20 to-emerald-300/5 text-emerald-700 dark:text-emerald-300",
    },
    {
      to: "/app/trades",
      label: "Open trade ledger",
      description: "Search, edit, and audit your recorded trading history.",
      icon: WalletCards,
      accent: "from-amber-500/20 to-amber-300/5 text-amber-700 dark:text-amber-300",
    },
  ];

  if (showUpgrade) {
    quickActions.push({
      to: "/app/upgrade",
      label: "Unlock pro workflows",
      description: "Activate advanced analytics, journal tools, and collaboration layers.",
      icon: Rocket,
      accent: "from-fuchsia-500/20 to-fuchsia-300/5 text-fuchsia-700 dark:text-fuchsia-300",
    });
  }

  const focusModules = [
    isPaid
      ? { title: "Goals and discipline", body: "Use structured targets and review loops to stay rule-based.", to: "/app/goals-challenges" }
      : { title: "Upgrade path", body: "Move from simple logging into disciplined, review-driven workflows.", to: "/app/upgrade" },
    hasTeam || user?.plan === "enterprise"
      ? { title: "Team visibility", body: "Stay aligned with your team workspace, messages, and shared activity.", to: "/app/team" }
      : { title: "Build your workspace", body: "Start with analytics, then layer in reviews, teams, and automation.", to: "/app/analytics" },
    isAdmin
      ? { title: "Admin control", body: "Manage plans, subscribers, and operational tooling from one place.", to: "/app/admin-panel" }
      : { title: "Trading journal", body: "Turn raw executions into repeatable patterns and measurable discipline.", to: "/app/journal" },
  ];

  return (
    <div className="space-y-8">
      <motion.section
        className="overflow-hidden rounded-[32px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.14),_transparent_25%),radial-gradient(circle_at_80%_20%,_rgba(14,165,233,0.12),_transparent_18%),linear-gradient(135deg,_#0f172a,_#111827_56%,_#1d4ed8)] px-6 py-7 text-white shadow-[0_40px_120px_-60px_rgba(15,23,42,0.95)] sm:px-8 sm:py-8"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <div className="grid gap-8 xl:grid-cols-[1.15fr_0.85fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.34em] text-sky-200">Trading Control Center</p>
            <h1 className="mt-4 max-w-3xl text-3xl font-semibold tracking-tight sm:text-4xl">
              {getDisplayName(user)}, run your journal like a serious performance business.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
              Track results, audit behavior, and move from scattered executions to a disciplined record that can support capital growth.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {metrics.map((metric) => (
                <div key={metric.label} className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-300">{metric.label}</p>
                  <div className={`mt-3 text-2xl font-semibold ${metric.tone}`}>{metric.value}</div>
                  <p className="mt-2 text-xs leading-6 text-slate-300">{metric.helper}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="rounded-[28px] border border-white/10 bg-black/20 p-5 backdrop-blur">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-slate-300">Session posture</p>
                <h2 className="mt-3 text-2xl font-semibold">Operational snapshot</h2>
              </div>
              <Sparkles className="h-6 w-6 text-amber-300" />
            </div>
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3 text-sm">
                <span className="text-slate-300">Plan tier</span>
                <span className="font-semibold text-white">{getPlanLabel(user)}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3 text-sm">
                <span className="text-slate-300">Team workspace</span>
                <span className="font-semibold text-white">{hasTeam ? "Connected" : "Solo mode"}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3 text-sm">
                <span className="text-slate-300">Workflow access</span>
                <span className="font-semibold text-white">{isPaid ? "Advanced" : "Core"}</span>
              </div>
            </div>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
              Use this workspace as your daily command center: log trades fast, review behavior often, and keep your decision-making visible.
            </div>
          </div>
        </div>
      </motion.section>

      <section className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <SurfaceCard>
          <SectionHeader
            eyebrow="Quick actions"
            title="Move fast without losing structure"
            description="Use the overview like a real control panel: log trades, inspect analytics, and move into the right workflow quickly."
          />
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {quickActions.map((action) => {
              const Icon = action.icon;

              return (
                <NavLink
                  key={action.label}
                  to={action.to}
                  className={`rounded-[24px] border border-slate-200 bg-gradient-to-br ${action.accent} p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg dark:border-gray-700`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="rounded-2xl bg-white/70 p-3 text-slate-900 shadow-sm dark:bg-gray-900/70 dark:text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <ChevronRight className="h-5 w-5 opacity-70" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-slate-950 dark:text-white">{action.label}</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{action.description}</p>
                </NavLink>
              );
            })}
          </div>
        </SurfaceCard>

        <SurfaceCard>
          <SectionHeader
            eyebrow="Focus modules"
            title="Where this journal should take you next"
            description="Keep the product outcome-oriented by pushing users into disciplined workflows, not random page visits."
          />
          <div className="mt-6 space-y-4">
            {focusModules.map((module) => (
              <NavLink
                key={module.title}
                to={module.to}
                className="block rounded-[24px] border border-slate-200 bg-slate-50 p-5 transition hover:border-slate-300 hover:bg-slate-100 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-950"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-950 dark:text-white">{module.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{module.body}</p>
                  </div>
                  <ChevronRight className="mt-1 h-5 w-5 text-slate-400" />
                </div>
              </NavLink>
            ))}
          </div>
        </SurfaceCard>
      </section>

      <section className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <SurfaceCard>
          <SectionHeader
            eyebrow="Performance Curve"
            title="Recent equity momentum"
            description="The last several trades should show whether the trader is compounding cleanly or leaking edge."
          />
          <div className="mt-6 h-[300px] w-full">
            {equityData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={equityData}>
                  <defs>
                    <linearGradient id="overviewEquity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" opacity={0.25} />
                  <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="equity" stroke="#0284c7" fill="url(#overviewEquity)" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                Add trades to unlock the live equity curve.
              </div>
            )}
          </div>
        </SurfaceCard>

        <div className="grid gap-4">
          <SurfaceCard>
            <SectionHeader eyebrow="Outcome Mix" title="Win-loss balance" description="This complements win rate with an at-a-glance outcome split." />
            <div className="mt-4 h-[220px] w-full">
              {outcomeData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={outcomeData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={3}>
                      {outcomeData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                  No recorded outcomes yet.
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {outcomeData.map((item) => (
                <StatusPill key={item.name} tone={item.name === "Wins" ? "success" : "danger"}>{item.name}: {item.value}</StatusPill>
              ))}
            </div>
          </SurfaceCard>

          <SurfaceCard>
            <SectionHeader eyebrow="Asset Concentration" title="What is driving returns" description="Top assets by visible P&L give immediate context for concentration and dependence." />
            <div className="mt-4 h-[220px] w-full">
              {assetBreakdown.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={assetBreakdown} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" opacity={0.2} />
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="asset" width={60} tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip />
                    <Bar dataKey="pnl" radius={[0, 10, 10, 0]}>
                      {assetBreakdown.map((item) => (
                        <Cell key={item.asset} fill={item.pnl >= 0 ? "#10b981" : "#f43f5e"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                  Asset concentration appears once trades exist.
                </div>
              )}
            </div>
          </SurfaceCard>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">

        <SurfaceCard>
          <SectionHeader
            eyebrow="Recent Activity"
            title="Live account feed"
            description="Notifications, team events, and system-level account changes should surface immediately on the overview page."
          />
          <div className="mt-6 space-y-4">
            {overviewFeed.length ? overviewFeed.map((item) => (
              <div key={item.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.title}</p>
                    <p className="mt-1 text-xs leading-6 text-slate-500 dark:text-slate-400">
                      {new Date(item.createdAt).toLocaleString()}{item.teamName ? ` · ${item.teamName}` : ""}
                    </p>
                  </div>
                  <StatusPill tone={item.tone || "neutral"}>
                    {item.kind === "notification" ? "Notification" : "Team Event"}
                  </StatusPill>
                </div>
                <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">{item.summary}</p>
              </div>
            )) : (
              <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                No overview activity yet. As notifications and team events appear, this feed will update automatically.
              </div>
            )}
          </div>
        </SurfaceCard>

        <SurfaceCard>
          <SectionHeader
            eyebrow="Workspace Readiness"
            title="Core operating metrics"
            description="These secondary numbers help frame scale, discipline, and available collaboration depth."
          />
          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <MetricCard label="Recorded trades" value={String(trades.length)} helper="Total journal entries currently available." />
            <MetricCard label="Team mode" value={hasTeam ? "Connected" : "Solo"} helper="Collaboration workspace status." />
            <MetricCard label="Workflow tier" value={isPaid ? "Advanced" : "Core"} helper="Available journaling and review depth." />
            <MetricCard label="Admin access" value={isAdmin ? "Enabled" : "None"} helper="Operational controls available for this account." />
            <MetricCard label="Upgrade path" value={showUpgrade ? "Open" : "Maxed"} helper="Whether there is an immediate commercial upgrade route." />
            <MetricCard label="Journal state" value={trades.length ? "Active" : "Empty"} helper="A quick read on whether data is flowing in." />
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button type="button" variant="secondary" onClick={() => window.location.assign("/app/trades")}>Open Ledger</Button>
            <Button type="button" variant="brand" onClick={() => window.location.assign("/app/newtrade")}>Log Next Trade</Button>
          </div>
        </SurfaceCard>
      </section>
    </div>
  );
}
